/**
 * Utility for lazy loading non-critical resources on mobile
 */

/**
 * Lazy load a script
 * @param {string} src - Script URL
 * @param {boolean} async - Whether to load asynchronously
 * @param {Function} callback - Callback function to execute when loaded
 */
export const lazyLoadScript = (src, async = true, callback = null) => {
  const script = document.createElement('script');
  script.src = src;
  script.async = async;
  
  if (callback) {
    script.onload = callback;
  }
  
  document.body.appendChild(script);
  
  return script;
};

/**
 * Lazy load a stylesheet
 * @param {string} href - Stylesheet URL
 * @param {Function} callback - Callback function to execute when loaded
 */
export const lazyLoadStylesheet = (href, callback = null) => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  
  if (callback) {
    link.onload = callback;
  }
  
  document.head.appendChild(link);
  
  return link;
};

/**
 * Lazy load an image
 * @param {string} src - Image URL
 * @param {Function} callback - Callback function to execute when loaded
 */
export const lazyLoadImage = (src, callback = null) => {
  const img = new Image();
  
  if (callback) {
    img.onload = callback;
  }
  
  img.src = src;
  
  return img;
};

/**
 * Detect if device is mobile
 * @returns {boolean} - True if mobile device
 */
export const isMobileDevice = () => {
  return window.innerWidth <= 768 || 
    navigator.userAgent.match(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i);
};

/**
 * Lazy load resources based on visibility
 * @param {Function} loadCallback - Function to call when element is visible
 * @param {Object} options - IntersectionObserver options
 * @returns {IntersectionObserver} - The observer instance
 */
export const createVisibilityObserver = (loadCallback, options = {}) => {
  const defaultOptions = {
    root: null, // viewport
    rootMargin: '0px',
    threshold: 0.1 // 10% visible
  };
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        loadCallback(entry.target);
        observer.unobserve(entry.target); // Stop observing once loaded
      }
    });
  }, { ...defaultOptions, ...options });
  
  return observer;
};

/**
 * Initialize lazy loading for the entire site
 * Only runs on mobile devices
 */
export const initLazyLoading = () => {
  if (!isMobileDevice()) return;
  
  // Lazy load all images with data-src attribute
  const lazyImages = document.querySelectorAll('img[data-src]');
  const imageObserver = createVisibilityObserver((img) => {
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  });
  
  lazyImages.forEach(img => imageObserver.observe(img));
  
  // Lazy load all background images with data-bg attribute
  const lazyBackgrounds = document.querySelectorAll('[data-bg]');
  const bgObserver = createVisibilityObserver((el) => {
    el.style.backgroundImage = `url(${el.dataset.bg})`;
    el.removeAttribute('data-bg');
  });
  
  lazyBackgrounds.forEach(bg => bgObserver.observe(bg));
  
  // Lazy load scripts with data-src attribute
  const lazyScripts = document.querySelectorAll('script[data-src]');
  lazyScripts.forEach(script => {
    lazyLoadScript(script.dataset.src, true);
    script.remove(); // Remove the placeholder
  });
};

